import { ref, computed, watch } from 'vue';
import { useI18n } from "vue-i18n";
import warehouseService, { type Warehouse } from '../services/warehouse';
import Swal from 'sweetalert2';

type WarehouseSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useWarehouse() {
  const { t } = useI18n();
  const warehouses = ref<Warehouse[]>([]);
  const searchQuery = ref('');
  const showModal = ref(false);
  const selectedWarehouse = ref<Warehouse | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});

  const form = ref<Warehouse>({
    code: '',
    name: '',
    phone: '',
    email: '',
    city: '',
    country: '',
    zip_code: '',
  });

  const fetchWarehouses = async () => {
    loading.value = true;
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error('Failed to fetch warehouses:', error);
      Swal.fire({
        icon: "error",
        title: t("warehouse_page.load_failed"),
        text: t("warehouse_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredWarehouses = computed(() =>
    searchQuery.value
      ? warehouses.value.filter((w) => {
          const query = searchQuery.value.toLowerCase();
          return [
            w.code,
            w.name,
            w.phone,
            w.email,
          ]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query));
        })
      : warehouses.value,
  );

  const pageSizeOptions = ref(['10', '25', '50', '100', '1000']);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("warehouse_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("warehouse_page.items_per_page") },
  }));

  watch(filteredWarehouses, (newVal) => {
    const totalCount = newVal.length > 0 ? newVal.length : 1000;
    pageSizeOptions.value = ['10', '25', '50', '100', String(totalCount)];
  }, { immediate: true });

  const openCreateModal = () => {
    selectedWarehouse.value = null;
    errors.value = {};
    form.value = {
      code: '',
      name: '',
      phone: '',
      address: '',
      email: '',
      city: '',
      country: '',
      zip_code: '',
      status: 1,
    };
    showModal.value = true;
  };

  const openEditModal = (warehouse: Warehouse) => {
    selectedWarehouse.value = warehouse;
    errors.value = {};
    form.value = { ...warehouse };
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
  };

  const updateField = (field: keyof Warehouse, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const generateWarehouseCode = async () => {
    try {
      if (!warehouses.value.length) {
        await fetchWarehouses();
      }

      const nextNumber =
        warehouses.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `WH${String(nextNumber).padStart(3, '0')}`;
    } catch (error) {
      console.error('Failed to generate warehouse code:', error);
      Swal.fire({
        icon: 'error',
        title: t("warehouse_page.generate_failed"),
        text: t("warehouse_page.generate_failed_text"),
      });
    }
  };

  const saveWarehouse = async () => {
    loading.value = true;
    errors.value = {};
    try {
      if (selectedWarehouse.value && selectedWarehouse.value.id) {
        await warehouseService.updateWarehouse(selectedWarehouse.value.id, form.value);
      } else {
        await warehouseService.createWarehouse(form.value);
      }
      await fetchWarehouses();
      await Swal.fire({
        icon: 'success',
        title: selectedWarehouse.value
          ? t("warehouse_page.updated_title")
          : t("warehouse_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as WarehouseSaveError;
      console.error(error);
      if (requestError.response && requestError.response.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        const errorMessage = requestError.response?.data?.message || t("warehouse_page.save_failed_text");
        Swal.fire({
          icon: 'error',
          title: t("warehouse_page.save_failed"),
          text: errorMessage as string,
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteWarehouse = async (warehouse: Warehouse) => {
    if (!warehouse?.id) return;

    const result = await Swal.fire({
      title: t("warehouse_page.delete_title"),
      text: t("warehouse_page.delete_text", { name: warehouse.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("warehouse_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("warehouse_page.deleting_title"),
        text: t("warehouse_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      await warehouseService.deleteWarehouse(warehouse.id);
      await fetchWarehouses();

      Swal.fire({
        icon: "success",
        title: t("warehouse_page.deleted_title"),
        text: t("warehouse_page.deleted_text", { name: warehouse.name }),
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("warehouse_page.delete_failed"),
        text: t("warehouse_page.delete_failed_text")
      });
    }
  };

  return {
    warehouses,
    searchQuery,
    filteredWarehouses,
    showModal,
    selectedWarehouse,
    form,
    errors,
    loading,
    paginationConfig,
    fetchWarehouses,
    openCreateModal,
    openEditModal,
    closeModal,
    saveWarehouse,
    updateField,
    generateWarehouseCode,
    deleteWarehouse,
  };
}
